/** Kryspace Control Bridge sidebar structure (consumer contract; derivation lives in kryspace). */

export type ControlBridgeStructureGroupId = 'domains' | 'domain_bridges' | 'specialty';

export interface ControlBridgeStructureNode {
  key: string;
  label: string;
  artifactInstanceId?: string;
  artifactTypeRef?: string;
  /**
   * Mission Control tile key (F1 / #13 emit). Prefer over label regex when present.
   * API camelCase ``mcDomainKey``; some orient payloads still use ``mc_domain_key``.
   */
  mcDomainKey?: 'business' | 'customer' | 'stakeholder';
  /** Snake_case alias from some thin-read / orient paths — prefer ``mcDomainKey``. */
  mc_domain_key?: 'business' | 'customer' | 'stakeholder';
  role?: string;
  filterScope: string;
  crosses?: string;
  desc?: string;
  /** Target kryspace workspace slug when node bridges to a component company model. */
  linkedWorkspaceSlug?: string;
  /** CB-DIB-002: what the linked sub-model is for. */
  purpose?: string;
  /** CB-DIB-002: open_workspace | read_only_rollup | delivery_queue. */
  recommendedAction?: string;
  children?: ControlBridgeStructureNode[];
}

export interface ControlBridgeStructureGroup {
  id: ControlBridgeStructureGroupId | string;
  label: string;
  nodes: ControlBridgeStructureNode[];
}

export interface KryspaceControlBridgeStructure {
  schemaVersion: string;
  workspaceSlug: string;
  generatedAt: string;
  companyTitle: string;
  rootInstanceId?: string;
  groups: ControlBridgeStructureGroup[];
}

function isStructureNode(value: unknown): value is ControlBridgeStructureNode {
  if (!value || typeof value !== 'object') return false;
  const n = value as ControlBridgeStructureNode;
  return (
    typeof n.key === 'string' &&
    typeof n.label === 'string' &&
    typeof n.filterScope === 'string'
  );
}

function isStructureGroup(value: unknown): value is ControlBridgeStructureGroup {
  if (!value || typeof value !== 'object') return false;
  const g = value as ControlBridgeStructureGroup;
  return typeof g.id === 'string' && typeof g.label === 'string' && Array.isArray(g.nodes);
}

export function isKryspaceControlBridgeStructure(value: unknown): value is KryspaceControlBridgeStructure {
  if (!value || typeof value !== 'object') return false;
  const s = value as KryspaceControlBridgeStructure;
  return (
    typeof s.schemaVersion === 'string' &&
    typeof s.workspaceSlug === 'string' &&
    typeof s.companyTitle === 'string' &&
    Array.isArray(s.groups) &&
    s.groups.every(isStructureGroup) &&
    s.groups.every((g) => g.nodes.every(isStructureNode))
  );
}
